import React, { useEffect, useState } from "react";
import { Button, Card } from "antd";
import { useTranslation } from "react-i18next";
import { useComplaints } from "../../../../../data/hooks/firestore/useComplaints";
import { useCustomers } from "../../../../../data/hooks/firestore/useCustomers";
import HorizontalBarChart from "../../../../../components/shared-components/HorizontalBarChart";
import { CURRENCY } from "../../../../../constants/DeliveryConstants";
import { formatPrice } from "../../../../../functions/formatters";
import { useNavigate, useSearchParams } from "react-router-dom";
import { complaintsReport } from "../../../../../urls";
import { COMPLAINT_REASONS } from "../../../../../constants/DepartmentConstants";
import dayjs from "dayjs";

const TopComplaintReasons = ({ dateRange, dateRangeText, companyFilter, marketingCodeFilter = "All" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: complaints, loading } = useComplaints(30, dateRange);
  const { customers, isLoading: customersLoading } = useCustomers();
  const [reasonsData, setReasonsData] = useState([]);

  useEffect(() => {
    if (complaints && dateRange && customers) {
      // Filter complaints by date range - convert to unix timestamps (seconds)
      const start = dayjs(dateRange[0]).startOf("day").unix();
      const end = dayjs(dateRange[1]).endOf("day").unix();

      let filteredComplaints = complaints.filter((complaint) => {
        const complaintDate = complaint.createdAt;
        return complaintDate >= start && complaintDate <= end;
      });

      // Apply company filter
      if (companyFilter !== "All") {
        filteredComplaints = filteredComplaints.filter((complaint) => {
          const customer = customers.find((c) => c.id === complaint.farmorderCustomerId);
          return customer?.company === companyFilter;
        });
      }

      // Apply marketing code filter
      if (marketingCodeFilter !== "All") {
        filteredComplaints = filteredComplaints.filter((complaint) => {
          const customer = customers.find((c) => c.id === complaint.farmorderCustomerId);
          return customer?.marketingCode === marketingCodeFilter;
        });
      }

      // Aggregate by reason
      const reasonsMap = {};

      filteredComplaints.forEach((complaint) => {
        if (complaint.products && Array.isArray(complaint.products)) {
          complaint.products.forEach((product) => {
            const reason = product.reason || "unknown";
            const amount = product.totalAmount || 0;
            const quantity = product.quantity || 0;

            if (reasonsMap[reason]) {
              reasonsMap[reason].totalAmount += amount;
              reasonsMap[reason].productCount += quantity;
            } else {
              reasonsMap[reason] = {
                reason,
                totalAmount: amount,
                productCount: quantity,
              };
            }
          });
        }
      });

      // Convert to array and sort by total amount
      const reasonsArray = Object.values(reasonsMap);
      const sortedReasons = reasonsArray.sort((a, b) => b.totalAmount - a.totalAmount).slice(0, 5);

      setReasonsData(sortedReasons);
    }
  }, [complaints, dateRange, customers, companyFilter, marketingCodeFilter]);

  const handleSeeFullReport = () => {
    const currentParams = searchParams.toString();
    navigate(`${complaintsReport}${currentParams ? `?${currentParams}` : ""}`);
  };

  // Get translated reason label
  const getReasonLabel = (reasonId) => {
    const reason = COMPLAINT_REASONS.find((r) => r.id === reasonId);
    return reason ? t(reason.labelKey) : reasonId;
  };

  if (!reasonsData || reasonsData.length === 0) {
    return (
      <Card
        title={t("top_complaint_reasons")}
        extra={
          <Button type="link" onClick={handleSeeFullReport}>
            {t("see_full_report")}
          </Button>
        }
        loading={loading || customersLoading}
      >
        <span className="text-muted">{dateRangeText}</span>
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <span className="text-muted">{t("no_data_available")}</span>
        </div>
      </Card>
    );
  }

  const series = [
    {
      name: t("credit_amount"),
      data: reasonsData.map((item) => item.totalAmount),
    },
  ];

  const categories = reasonsData.map((item) => getReasonLabel(item.reason));

  const formatValue = (val) => CURRENCY + formatPrice(val);

  return (
    <Card
      title={t("top_complaint_reasons")}
      extra={
        <Button type="link" onClick={handleSeeFullReport}>
          {t("see_full_report")}
        </Button>
      }
      loading={loading}
    >
      <span className="text-muted">{dateRangeText}</span>
      <div style={{ marginTop: 16 }}>
        {reasonsData.map((item) => (
          <div
            key={item.reason}
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
              fontSize: "12px",
            }}
          >
            <span className="text-muted">
              {getReasonLabel(item.reason)}: {item.productCount} {t("products")}
            </span>
          </div>
        ))}
      </div>
      <HorizontalBarChart series={series} categories={categories} height={250} formatValue={formatValue} />
    </Card>
  );
};

export default TopComplaintReasons;
